// CMS Reports Page
import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useCMS } from './CMSContext';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { 
  Users, DollarSign, ShoppingCart, TrendingUp, RefreshCw, Globe, Ticket
} from 'lucide-react';
import {
  PieChart, Pie, Cell, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, 
  ResponsiveContainer, Legend
} from 'recharts';
import { toast } from 'sonner';
import { getStatusHex, getStatusMeta } from '../lib/bookingStatus';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const COLORS = ['#3b82f6', '#22c55e', '#f59e0b', '#ef4444', '#8b5cf6', '#06b6d4'];

const CMSReports = () => {
  const { getAuthHeaders } = useCMS();
  const [period, setPeriod] = useState('month');
  const [userReport, setUserReport] = useState(null);
  const [revenueReport, setRevenueReport] = useState(null);
  const [bookingReport, setBookingReport] = useState(null);
  const [loading, setLoading] = useState(true);

  const fetchReports = async () => {
    setLoading(true);
    try {
      const [users, revenue, bookings] = await Promise.all([
        axios.get(`${API}/cms/reports/users?period=${period}`, { headers: getAuthHeaders() }),
        axios.get(`${API}/cms/reports/revenue?period=${period}`, { headers: getAuthHeaders() }),
        axios.get(`${API}/cms/reports/bookings?period=${period}`, { headers: getAuthHeaders() })
      ]);
      setUserReport(users.data);
      setRevenueReport(revenue.data);
      setBookingReport(bookings.data);
    } catch (err) {
      toast.error('Failed to load reports');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchReports();
  }, [period]);

  const periodLabels = {
    week: 'Last 7 Days',
    month: 'Last 30 Days',
    quarter: 'Last 90 Days',
    year: 'Last Year'
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold">Reports</h1>
          <p className="text-slate-400">Analytics and insights</p>
        </div>
        <div className="flex items-center gap-4">
          <Select value={period} onValueChange={setPeriod}>
            <SelectTrigger className="w-48 bg-slate-800 border-slate-700">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="week">Last 7 Days</SelectItem>
              <SelectItem value="month">Last 30 Days</SelectItem>
              <SelectItem value="quarter">Last 90 Days</SelectItem>
              <SelectItem value="year">Last Year</SelectItem>
            </SelectContent>
          </Select>
          <Button variant="outline" onClick={fetchReports} disabled={loading}>
            <RefreshCw className={`w-4 h-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
            Refresh
          </Button>
        </div>
      </div>

      {loading ? (
        <div className="flex items-center justify-center h-96">
          <RefreshCw className="w-8 h-8 animate-spin text-primary" />
        </div>
      ) : (
        <Tabs defaultValue="users" className="space-y-6">
          <TabsList className="bg-slate-800">
            <TabsTrigger value="users"><Users className="w-4 h-4 mr-2" />Users</TabsTrigger>
            <TabsTrigger value="revenue"><DollarSign className="w-4 h-4 mr-2" />Revenue</TabsTrigger>
            <TabsTrigger value="bookings"><ShoppingCart className="w-4 h-4 mr-2" />Bookings</TabsTrigger>
          </TabsList>

          {/* Users Report */}
          <TabsContent value="users" className="space-y-6">
            {/* Summary Cards */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <Card className="bg-slate-800 border-slate-700">
                <CardContent className="p-6">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm text-slate-400">New Users</p>
                      <p className="text-3xl font-bold">{userReport?.new_users || 0}</p>
                    </div>
                    <div className="p-3 bg-blue-500/10 rounded-lg">
                      <Users className="w-6 h-6 text-blue-400" />
                    </div>
                  </div>
                  <p className="text-xs text-slate-500 mt-2">{periodLabels[period]}</p>
                </CardContent>
              </Card>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              {/* Users by Country */}
              <Card className="bg-slate-800 border-slate-700">
                <CardHeader>
                  <CardTitle className="text-lg flex items-center gap-2">
                    <Globe className="w-5 h-5" />
                    Users by Country
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="h-64">
                    {userReport?.by_country?.length > 0 ? (
                      <ResponsiveContainer width="100%" height="100%">
                        <BarChart data={userReport.by_country} layout="vertical">
                          <CartesianGrid strokeDasharray="3 3" stroke="#334155" />
                          <XAxis type="number" stroke="#94a3b8" />
                          <YAxis dataKey="country" type="category" stroke="#94a3b8" width={80} />
                          <Tooltip 
                            contentStyle={{ backgroundColor: '#1e293b', border: '1px solid #334155' }}
                          />
                          <Bar dataKey="count" fill="#3b82f6" radius={[0, 4, 4, 0]} />
                        </BarChart>
                      </ResponsiveContainer>
                    ) : (
                      <p className="text-center text-slate-400 py-8">No data available</p>
                    )}
                  </div>
                </CardContent>
              </Card>

              {/* Pass Distribution */}
              <Card className="bg-slate-800 border-slate-700">
                <CardHeader>
                  <CardTitle className="text-lg flex items-center gap-2">
                    <Ticket className="w-5 h-5" />
                    Pass Distribution
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="h-64">
                    {userReport?.pass_distribution?.length > 0 ? (
                      <ResponsiveContainer width="100%" height="100%">
                        <PieChart>
                          <Pie
                            data={userReport.pass_distribution}
                            dataKey="count"
                            nameKey="type"
                            cx="50%"
                            cy="50%"
                            outerRadius={80}
                            label={({ type, count }) => `${type}: ${count}`}
                          >
                            {userReport.pass_distribution.map((_, index) => (
                              <Cell key={index} fill={COLORS[index % COLORS.length]} />
                            ))}
                          </Pie>
                          <Tooltip 
                            contentStyle={{ backgroundColor: '#1e293b', border: '1px solid #334155' }}
                          />
                          <Legend />
                        </PieChart>
                      </ResponsiveContainer>
                    ) : (
                      <p className="text-center text-slate-400 py-8">No data available</p>
                    )}
                  </div>
                </CardContent>
              </Card>
            </div>
          </TabsContent>

          {/* Revenue Report */}
          <TabsContent value="revenue" className="space-y-6">
            {/* Summary Cards */}
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
              <Card className="bg-slate-800 border-slate-700">
                <CardContent className="p-6">
                  <p className="text-sm text-slate-400">Total Revenue</p>
                  <p className="text-3xl font-bold text-green-400">€{revenueReport?.total_revenue?.toLocaleString() || 0}</p>
                </CardContent>
              </Card>
              <Card className="bg-slate-800 border-slate-700">
                <CardContent className="p-6">
                  <p className="text-sm text-slate-400">Payments</p>
                  <p className="text-3xl font-bold">{revenueReport?.payment_count || 0}</p>
                </CardContent>
              </Card>
              <Card className="bg-slate-800 border-slate-700">
                <CardContent className="p-6">
                  <p className="text-sm text-slate-400">Total Refunds</p>
                  <p className="text-3xl font-bold text-red-400">€{revenueReport?.total_refunds?.toLocaleString() || 0}</p>
                </CardContent>
              </Card>
              <Card className="bg-slate-800 border-slate-700">
                <CardContent className="p-6">
                  <p className="text-sm text-slate-400">Net Revenue</p>
                  <p className="text-3xl font-bold text-emerald-400">€{revenueReport?.net_revenue?.toLocaleString() || 0}</p>
                </CardContent>
              </Card>
            </div>

            {/* Revenue by Type */}
            <Card className="bg-slate-800 border-slate-700">
              <CardHeader>
                <CardTitle className="text-lg">Revenue by Pass Type</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="h-64">
                  {revenueReport?.by_type?.length > 0 ? (
                    <ResponsiveContainer width="100%" height="100%">
                      <BarChart data={revenueReport.by_type}>
                        <CartesianGrid strokeDasharray="3 3" stroke="#334155" />
                        <XAxis dataKey="type" stroke="#94a3b8" />
                        <YAxis stroke="#94a3b8" />
                        <Tooltip 
                          contentStyle={{ backgroundColor: '#1e293b', border: '1px solid #334155' }}
                          formatter={(value) => [`€${value}`, 'Revenue']}
                        />
                        <Bar dataKey="total" fill="#22c55e" radius={[4, 4, 0, 0]} />
                      </BarChart>
                    </ResponsiveContainer>
                  ) : (
                    <p className="text-center text-slate-400 py-8">No data available</p>
                  )}
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Bookings Report */}
          <TabsContent value="bookings" className="space-y-6">
            {/* Summary Cards */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <Card className="bg-slate-800 border-slate-700">
                <CardContent className="p-6">
                  <p className="text-sm text-slate-400">Total Bookings</p>
                  <p className="text-3xl font-bold">{bookingReport?.total_bookings || 0}</p>
                </CardContent>
              </Card>
              <Card className="bg-slate-800 border-slate-700">
                <CardContent className="p-6">
                  <p className="text-sm text-slate-400">Average Value</p>
                  <p className="text-3xl font-bold">€{bookingReport?.average_value?.toLocaleString() || 0}</p>
                </CardContent>
              </Card>
              <Card className="bg-slate-800 border-slate-700">
                <CardContent className="p-6">
                  <p className="text-sm text-slate-400">Total Value</p>
                  <p className="text-3xl font-bold text-green-400">€{bookingReport?.total_value?.toLocaleString() || 0}</p>
                </CardContent>
              </Card>
            </div>

            {/* Bookings by Status */}
            <Card className="bg-slate-800 border-slate-700">
              <CardHeader>
                <CardTitle className="text-lg">Bookings by Status</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="h-64">
                  {bookingReport?.by_status?.length > 0 ? (
                    <ResponsiveContainer width="100%" height="100%">
                      <PieChart>
                        <Pie
                          data={bookingReport.by_status}
                          dataKey="count"
                          nameKey="_id"
                          cx="50%"
                          cy="50%"
                          outerRadius={80}
                          label={({ _id, count }) => `${getStatusMeta(_id).label}: ${count}`}
                        >
                          {bookingReport.by_status.map((entry, index) => (
                            <Cell key={index} fill={getStatusHex(entry._id)} />
                          ))}
                        </Pie>
                        <Tooltip 
                          contentStyle={{ backgroundColor: '#1e293b', border: '1px solid #334155' }}
                          formatter={(value, _name, props) => [value, getStatusMeta(props?.payload?._id).label]}
                        />
                        <Legend formatter={(value) => getStatusMeta(value).label} />
                      </PieChart>
                    </ResponsiveContainer>
                  ) : (
                    <p className="text-center text-slate-400 py-8">No data available</p>
                  )}
                </div>
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      )}
    </div>
  );
};

export default CMSReports;
